import { useQueryClient } from "@tanstack/react-query";
import { useEffect, type ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { ApiError, UNAUTHORISED_EVENT } from "@otii/ui/api";
import { ErrorNote, Loading } from "@otii/ui/ui";
import { useMe } from "../../lib/queries";

/** Requires a session before rendering the app. */
export function AuthGate({ children }: { children: ReactNode }) {
  const me = useMe();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  useEffect(() => {
    const onUnauthorised = () => {
      qc.clear();
      navigate(`/sign-in?next=${encodeURIComponent(location.pathname + location.search)}`);
    };
    window.addEventListener(UNAUTHORISED_EVENT, onUnauthorised);
    return () => window.removeEventListener(UNAUTHORISED_EVENT, onUnauthorised);
  }, [qc, navigate, location]);

  if (me.isLoading)
    return (
      <div className="grid min-h-screen place-items-center bg-cream">
        <Loading label="Opening…" />
      </div>
    );
  if (me.error) {
    if (me.error instanceof ApiError && me.error.status === 401) return <Navigate to={`/sign-in?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
    return (
      <div className="mx-auto max-w-md p-8">
        <ErrorNote error={me.error} />
      </div>
    );
  }
  return <>{children}</>;
}
